export function normalize<T extends { id: string | number; slug?: string }>(
  entities: T[],
  existingEntities: Record<string | number, T> = {},
  existingSlugMap: Record<string, string | number> = {}
): {
  entities: Record<string | number, T>;
  slugMap: Record<string, string | number>;
  ids: (string | number)[];
} {
  const ids: (string | number)[] = [];
  const newEntities = { ...existingEntities };
  const slugMap = { ...existingSlugMap };

  entities.forEach(entity => {
    ids.push(entity.id);
    newEntities[entity.id] = entity;
    if (entity.slug) {
      slugMap[entity.slug] = entity.id;
    }
  });

  return { entities: newEntities, slugMap, ids };
}

export function denormalize<T>(
  ids: (string | number)[],
  entities: Record<string | number, T>
): T[] {
  if (!ids) return [];
  return ids.map(id => entities[id]).filter(Boolean);
}

/**
 * Constructs a relative recipe URL with environment parameter for tracking
 * ONLY for Strapi recipes - GitHub recipes are excluded
 *
 * Backend will prepend "https://app.zerops.io/recipes/" and append "#next-steps"
 * to create the final URL used in success emails.
 *
 * @param recipeSlug - Recipe slug identifier (e.g., 'laravel', 'zerops-showcase')
 * @param environmentName - Environment/variant name (e.g., 'production', 'small-production')
 * @returns Relative URL with environment parameter, or undefined if data incomplete
 *
 * @example
 * buildRecipeSourceUrl('zerops-showcase', 'small-production')
 * // Returns: "zerops-showcase?environment=small-production"
 * // Backend constructs: "https://app.zerops.io/recipes/zerops-showcase?environment=small-production#next-steps"
 */
export function buildRecipeSourceUrl(
  recipeSlug: string,
  environmentName: string
): string | undefined {
  if (!recipeSlug || !environmentName) return undefined;

  return `${recipeSlug}?environment=${environmentName}`;
}

/**
 * Extracts recipe source identifier from GitHub URL
 * Used for populating recipeSource field
 *
 * @param githubUrl - Full GitHub URL
 * @returns "owner/repo" or undefined
 *
 * @example
 * extractRecipeSource('https://github.com/zeropsio/recipe-laravel')
 * // Returns: "zeropsio/recipe-laravel"
 */
export function extractRecipeSource(githubUrl?: string): string | undefined {
  if (!githubUrl) return undefined;
  const match = githubUrl.match(/github\.com\/(.+?)(?:\.git)?$/);
  return match?.[1];
}
